import { Chart as ChartJS, Tooltip } from 'chart.js';

// Shared chart color palette + Chart.js base options for every report
// dashboard (PlatformReport.js, LendReportSections.js) - previously
// duplicated byte-for-byte between the two files; consolidated here so they
// can't drift out of sync by hand. Mirrors pdf_generation_service's own
// per-service _chart_config.py copies (same colors, kept in sync manually
// there since Python/JS can't share a module across services the way these
// two same-language files now do).
// Exaloan_Brand_Guidelines.pdf's official palette: Teal #077186 (primary -
// "the main color, strongest presence on our brand"), Midnight Express navy
// #0f1942 (secondary), Raven #676c76 (balances the other colors), Riptide
// #9bd5c4 (secondary/accent, used when the primary colors don't fit). This
// is the SHARED, generic chart palette used across every platform's charts
// (sector/purpose/vintage/etc.) - not the same thing as a given platform's
// own color_primary/secondary/accent (pdf_generation_service/_chart_config.py
// PLATFORM_CONFIG), which is deliberately sourced from that platform's own
// reference report and stays untouched by this rebrand.
//
// categorical[0]/[1] (teal/navy) are also referenced directly by name
// (PlatformReport.js's PALETTE_FN_SECTOR) rather than just by position, so
// the "largest slice darkest" sector gradient keeps navy as its dark anchor
// regardless of how this array is reordered - see the comment there.
export const CHART_COLORS = {
    categorical: ['#077186', '#0f1942', '#676c76', '#9bd5c4', '#0a95ad', '#2d4a63', '#8a9099', '#c3e4d8', '#054f5c', '#4d8fa0'],
    sequential_blue: ['#e7f3f1', '#c3e0da', '#9dc9c0', '#75b0a6', '#54968d', '#377b76', '#215f5f', '#123f45', '#0f1942'],
    vintage_line: ['#077186', '#0f1942', '#676c76', '#9bd5c4'],
    repayment_status: { defaulted: '#0f1942', principal_outstanding: '#f2b880', principal_paid_cashflows: '#9bd5c4' },
};

// Every chart legend across the app, regardless of what raw field/category
// value fed a chart's dataset.label or data.labels (e.g.
// "principal_outstanding", "defaulted", "sector_NACE_1_description" values) -
// none of those are pre-formatted for display, so this is applied once,
// globally, via applyChartJsTheme's generateLabels override below rather
// than hunting down each chart's own label-construction site.
//
// Short, already-fully-uppercase tokens (credit ratings AAA/BB/CCC/D,
// borrower type SME, ...) are left untouched - sentence-casing those to
// "Aaa"/"Sme" would misrepresent a rating grade or a borrower-type name as
// if it were just a snake_case field value that needed readable casing.
export function formatLegendText(text) {
    if (typeof text !== 'string' || !text) return text;
    if (text.length <= 5 && /^[A-Z0-9]+$/.test(text)) return text;
    const spaced = text.replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

// Chart.js canvas-reuse workaround: primereact's <Chart> keeps the same
// underlying canvas/chart instance across renders and calls chart.update()
// rather than destroying and recreating it. When two consecutive renders are
// structurally unrelated - a different category set (data.labels) or a
// different series set (each dataset's own label, for charts whose series
// are derived from raw data - buckets, vintages, sectors - rather than a
// small fixed literal list) - Chart.js's own update/animation diffing isn't
// guaranteed to fully clear indices that don't correspond to anything in the
// new data, leaving stale slices/legend entries/series behind (confirmed:
// switching between two platforms' sector breakdowns, whose category sets
// are structurally unrelated, left one platform's slices visible alongside
// the other's). Pass this as the React `key` on the <Chart> element itself -
// forces a full remount (fresh canvas) whenever the actual shape changes,
// while ordinary value updates within the same categories/series still
// animate smoothly as before, since the key stays stable.
export function chartRemountKey(data) {
    return `${(data.labels || []).join('|')}::${(data.datasets || []).map((d) => d.label ?? '').join('|')}`;
}

// Cycles through a fixed palette rather than generating arbitrary colors -
// same tradeoff the PDF side accepts (e.g. vintage_line has 4 entries and
// just repeats for a 5th+ vintage year) so the two stay comparable instead
// of the dashboard inventing shades the PDF never uses.
export function cyclePalette(palette, n) {
    return Array.from({ length: n }, (_, i) => palette[i % palette.length]);
}

// Brand Guidelines: Verlag is the primary typeface, Arial the fallback when
// Verlag isn't available - Chart.js's root-level `font` option cascades to
// axis ticks, legend labels and tooltips, same as most text elements here.
export const BASE_OPTIONS = { maintainAspectRatio: false, responsive: true, font: { family: 'Verlag, Arial, sans-serif' }, plugins: { legend: { labels: { boxWidth: 12, font: { family: 'Verlag, Arial, sans-serif' } } } } };

// CHART_COLORS' own data-series palette (categorical/sequential_blue/...)
// mostly does NOT change with theme - most of those hues are brand identity
// for the data itself, not UI chrome, and stay legible on both light and
// dark canvases without inverting. The exception: a handful of the
// palette's darkest entries (Midnight Express navy #0f1942 chief among
// them - categorical[1]/vintage_line[1]/sequential_blue's darkest step/
// repayment_status.defaulted all reuse it) have essentially the same hue
// and lightness as the dark theme's own card background
// (exaloan-primereact-theme-dark.css's --surface-a) - measured contrast as
// low as 1.03:1 against it, i.e. functionally invisible, not just "a bit
// dark". DARK_COLOR_OVERRIDES below remaps exactly those specific values
// (same hue/saturation, lightened until contrast against the dark card
// clears ~4.5:1) via applyChartColorsTheme - everything else in the
// palette is untouched. What else genuinely needs to change in dark
// mode is Chart.js's surrounding chrome - axis tick/label text, gridlines
// and the legend's own label text - which otherwise default to Chart.js's
// own near-black, unreadable against a dark card background.
//
// Original (light-mode) values, frozen here so applyChartColorsTheme can
// always remap from a known-good starting point rather than compounding
// repeated dark->dark calls (e.g. a theme toggled back and forth).
const LIGHT_CATEGORICAL = ['#077186', '#0f1942', '#676c76', '#9bd5c4', '#0a95ad', '#2d4a63', '#8a9099', '#c3e4d8', '#054f5c', '#4d8fa0'];
const LIGHT_SEQUENTIAL_BLUE = ['#e7f3f1', '#c3e0da', '#9dc9c0', '#75b0a6', '#54968d', '#377b76', '#215f5f', '#123f45', '#0f1942'];
const LIGHT_VINTAGE_LINE = ['#077186', '#0f1942', '#676c76', '#9bd5c4'];
const LIGHT_REPAYMENT_STATUS = { defaulted: '#0f1942', principal_outstanding: '#f2b880', principal_paid_cashflows: '#9bd5c4' };

// Each computed via colorsys: same hue as the original, lightness/
// saturation tuned until WCAG contrast against #111833 (the dark card
// background) reached >=4.5:1 - not arbitrary picks. #0f1942's replacement
// specifically was asked to feel closer to --app-bg's own muted slate
// (#54596f) rather than the first attempt's vivid periwinkle - but
// --app-bg's exact value only clears 2.5:1 against the card (still too low)
// and is nearly indistinguishable from this same palette's own mortgage-
// gray (#8a8f98, ~1.2:1 apart) in the Lend charts that show both side by
// side - using it verbatim would partially reintroduce the invisibility bug
// and make two different series look identical. #7881a5 keeps --app-bg's
// hue family and a similarly muted (not vivid) feel, at a lightness/
// saturation combination chosen to still clear 4.5:1 against the card.
const DARK_COLOR_OVERRIDES = {
    '#0f1942': '#7881a5',
    '#2d4a63': '#5486b1',
    '#054f5c': '#0991a9',
    '#123f45': '#29909e',
    '#215f5f': '#329090',
};

function remapForDark(palette, isDark) {
    return palette.map((c) => (isDark && DARK_COLOR_OVERRIDES[c]) || c);
}

// Mutates CHART_COLORS in place - every consumer (PALETTE_FN_CATEGORICAL/
// PALETTE_FN_SEQUENTIAL_BLUE/PALETTE_FN_SECTOR in PlatformReport.js, the
// various `cyclePalette(CHART_COLORS.categorical, ...)` calls in
// LendReportSections.js) reads CHART_COLORS.categorical etc. fresh every
// time a chart renders, not a value captured once at module load - so
// mutating the underlying arrays here is picked up by every chart the next
// time it renders, without needing to touch any of those ~10 call sites or
// thread `theme` through them individually. Call alongside
// applyChartJsTheme in the same effect.
export function applyChartColorsTheme(theme) {
    const isDark = theme === 'dark';
    CHART_COLORS.categorical = remapForDark(LIGHT_CATEGORICAL, isDark);
    CHART_COLORS.sequential_blue = remapForDark(LIGHT_SEQUENTIAL_BLUE, isDark);
    CHART_COLORS.vintage_line = remapForDark(LIGHT_VINTAGE_LINE, isDark);
    CHART_COLORS.repayment_status = {
        defaulted: (isDark && DARK_COLOR_OVERRIDES[LIGHT_REPAYMENT_STATUS.defaulted]) || LIGHT_REPAYMENT_STATUS.defaulted,
        principal_outstanding: LIGHT_REPAYMENT_STATUS.principal_outstanding,
        principal_paid_cashflows: LIGHT_REPAYMENT_STATUS.principal_paid_cashflows,
    };
}
//
// Set as Chart.js's own global defaults (ChartJS.defaults.*) rather than
// spread into each chart's own `options` object: every chart across
// PlatformReport.js/LendReportSections.js (~35 call sites) already builds
// its own options as `{...BASE_OPTIONS, scales: {...}, plugins: {...}}` -
// a plain JS spread, so injecting theme colors that way would need a real
// deep merge at every single one of those sites to avoid a chart's own
// `scales`/`plugins` override silently dropping the theme colors entirely.
// Chart.js resolves `Chart.defaults` against each chart's own (partial)
// options property-by-property internally, not via a shallow spread, so a
// global default here is picked up correctly no matter what each chart's
// own options object does or doesn't set - zero risk of clobbering any
// existing per-chart override, and one call site rather than ~35. Mirrors
// exaloan-primereact-theme-dark.css's own --text-color/--surface-border
// dark values so charts stay visually consistent with the rest of the page.
export function applyChartJsTheme(theme) {
    const isDark = theme === 'dark';
    // UI chrome only (axis/legend text, gridlines, tooltip box) - tuned to
    // the app's --app-text-muted/--app-border tokens (main.css) so charts
    // sit quietly inside the page's cards. Data-series colors are untouched.
    const textColor = isDark ? 'rgba(226, 231, 243, 0.86)' : '#4a5470';
    const gridColor = isDark ? 'rgba(163, 173, 196, 0.16)' : '#e8ecf2';
    ChartJS.defaults.color = textColor;
    ChartJS.defaults.borderColor = gridColor;
    // Chart.js only populates defaults.scale/defaults.plugins.legend once
    // some chart controller/plugin has actually been registered (LendReport
    // Sections.js only registers BoxPlotController/BoxAndWiskers up front -
    // Legend/the standard scale defaults are registered lazily by
    // primereact/chart's own <Chart> the first time one actually renders).
    // This effect runs on mount, before any chart has necessarily rendered
    // yet, so defaults.plugins.legend can genuinely be undefined here -
    // build the path defensively rather than assuming it pre-exists (this
    // crashed the whole PlatformReport page on first load otherwise).
    // Chart.js merges its own real defaults into whatever's already there
    // when a Legend-using chart does register, so seeding a partial object
    // here isn't overwritten, just filled in alongside it.
    if (ChartJS.defaults.scale) {
        ChartJS.defaults.scale.grid = { ...ChartJS.defaults.scale.grid, color: gridColor };
        ChartJS.defaults.scale.ticks = { ...ChartJS.defaults.scale.ticks, color: textColor };
    }
    ChartJS.defaults.plugins = ChartJS.defaults.plugins || {};
    // Tooltip box styling. Registered here first (a no-op if chart.js/auto
    // already registered it) so Chart.js's own tooltip defaults are merged
    // in BEFORE these overrides rather than on top of them later - see
    // chart.js's registerDefaults, which merges a plugin's own defaults last.
    ChartJS.register(Tooltip);
    // Mutated in place (not replaced) so anything else Chart.js keeps on
    // this defaults object stays exactly as registered.
    ChartJS.defaults.plugins.tooltip = ChartJS.defaults.plugins.tooltip || {};
    Object.assign(ChartJS.defaults.plugins.tooltip, {
        backgroundColor: isDark ? 'rgba(23, 32, 66, 0.96)' : 'rgba(15, 25, 66, 0.92)',
        borderColor: isDark ? 'rgba(155, 213, 196, 0.35)' : 'rgba(15, 25, 66, 0.92)',
        borderWidth: 1,
        titleColor: '#ffffff',
        bodyColor: 'rgba(255, 255, 255, 0.9)',
        padding: 10,
        cornerRadius: 8,
        boxPadding: 4,
        titleFont: { family: 'Verlag, Arial, sans-serif', weight: 'bold' },
        bodyFont: { family: 'Verlag, Arial, sans-serif' },
    });
    ChartJS.defaults.plugins.legend = ChartJS.defaults.plugins.legend || {};
    ChartJS.defaults.plugins.legend.labels = {
        ...ChartJS.defaults.plugins.legend.labels,
        color: textColor,
        // Post-processes Chart.js's own built-in generateLabels rather than
        // reimplementing it - fillStyle/strokeStyle/hidden/index differ by
        // chart type (pie/doughnut derive per-slice from data.labels, bar/
        // line derive per-dataset), so only .text is touched here. Read from
        // the legend plugin's own registered defaults (registry), not
        // ChartJS.defaults.plugins.legend.labels.generateLabels - that's
        // this exact assignment, so reading it back here would just
        // recurse into itself instead of Chart.js's real implementation.
        generateLabels: (chart) => {
            const items = ChartJS.registry.getPlugin('legend').defaults.labels.generateLabels(chart);
            return items.map((item) => ({ ...item, text: formatLegendText(item.text) }));
        },
    };
}
